import React, { useEffect, useState } from 'react';
import { Card, Button, Input } from '../../shared/design';
import { getBackendService } from '../../shared/services/apiConfig';
import { USE_FIREBASE } from '../../shared/services/apiConfig';
import { firestoreService } from '../../shared/services/firestoreService';
import { DEFAULT_SEGMENT_RULES, ALL_SEGMENTS, SEGMENT_LABELS, describeSegment } from '../../shared/customerSegmentation';
import type { SegmentRulesDoc } from '../../shared/types';
import SegmentRulesEditor from './SegmentRulesEditor';

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
    <Card padding="none" className="overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
            {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className="px-6 divide-y divide-gray-100 dark:divide-gray-800">{children}</div>
    </Card>
);

const Row: React.FC<{ label: string; description?: string; children: React.ReactNode }> = ({ label, description, children }) => (
    <div className="flex items-center justify-between py-4 gap-4">
        <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</p>
            {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>}
        </div>
        <div className="flex-shrink-0">{children}</div>
    </div>
);

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void }> = ({ checked, onChange }) => (
    <button
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-brand-primary' : 'bg-gray-200 dark:bg-gray-700'}`}
    >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
);

const SegmentationSection: React.FC = () => {
    const [restaurantId, setRestaurantId] = useState<string | null>(null);
    const [rulesDoc, setRulesDoc] = useState<SegmentRulesDoc>({ rules: DEFAULT_SEGMENT_RULES, lastComputedAt: null });
    const [loaded, setLoaded] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);

    const load = async (rid: string) => {
        const doc = await firestoreService.getSegmentRules(rid);
        setRulesDoc(doc);
    };

    useEffect(() => {
        let isMounted = true;
        (async () => {
            const backend = await getBackendService();
            const user = backend.getCurrentUser();
            if (!user?.restaurantId) { if (isMounted) setLoaded(true); return; }
            const ridStr = String(user.restaurantId);
            if (isMounted) setRestaurantId(ridStr);
            try { if (isMounted) await load(ridStr); } catch { /* défauts */ }
            if (isMounted) setLoaded(true);
        })();
        return () => { isMounted = false; };
    }, []);

    return (
        <Section
            title="Segmentation client"
            subtitle="Les clients sont classés chaque nuit (Nouveau, Habitué, VIP, Gros panier, À risque) selon vos seuils. Visible uniquement par vous."
        >
            {ALL_SEGMENTS.map(seg => (
                <Row key={seg} label={SEGMENT_LABELS[seg]} description={describeSegment(seg, rulesDoc.rules)}>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                        {rulesDoc.rules[seg === 'gros_panier' ? 'grosPanier' : seg === 'a_risque' ? 'aRisque' : seg].enabled ? 'Actif' : 'Désactivé'}
                    </span>
                </Row>
            ))}
            <div className="py-4 flex items-center gap-3 flex-wrap">
                <Button variant="primary" onClick={() => setEditorOpen(true)} disabled={!loaded || !restaurantId || rulesDoc.loadFailed === true}>
                    Modifier les seuils
                </Button>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                    Dernier calcul : {rulesDoc.lastComputedAt ? rulesDoc.lastComputedAt.toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'pas encore'}
                </span>
                {notice && <span role="status" className="text-sm text-green-600">{notice}</span>}
                {rulesDoc.loadFailed && <span role="alert" className="text-sm text-red-600">Règles illisibles pour le moment, rechargez la page.</span>}
                {!restaurantId && loaded && <span className="text-xs text-gray-400">Aucun restaurant associé à votre compte.</span>}
            </div>
            {editorOpen && restaurantId && (
                <SegmentRulesEditor
                    restaurantId={restaurantId}
                    initialRules={rulesDoc.rules}
                    onClose={() => setEditorOpen(false)}
                    onSaved={async (recomputed, warning) => {
                        setEditorOpen(false);
                        setNotice(warning ?? (recomputed ? 'Seuils enregistrés et segments recalculés.' : 'Seuils enregistrés, appliqués au prochain calcul de nuit.'));
                        try { await load(restaurantId); } catch { /* ignore */ }
                    }}
                />
            )}
        </Section>
    );
};

const SettingsPage: React.FC = () => {
    const [notifEmail, setNotifEmail] = useState(true);
    const [notifPush, setNotifPush] = useState(true);
    const [notifSms, setNotifSms] = useState(false);
    const [autoExport, setAutoExport] = useState(false);
    const [language, setLanguage] = useState('fr');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        await new Promise(r => setTimeout(r, 800));
        setSaving(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
    };

    return (
        <div className="space-y-8 animate-fade-in max-w-3xl">
            <div>
                <h1 className="page-title">Paramètres</h1>
                <p className="page-subtitle">Gérez les préférences de votre espace restaurateur</p>
            </div>

            {/* Compte */}
            <Section title="Compte" subtitle="Informations de connexion">
                <Row label="Email" description="Adresse email associée au compte">
                    <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                        {USE_FIREBASE ? '(Firebase Auth)' : 'non disponible en mode démo'}
                    </span>
                </Row>
                <Row label="Mot de passe" description="Changer votre mot de passe">
                    <button className="text-sm font-medium text-brand-primary hover:underline">
                        Modifier
                    </button>
                </Row>
            </Section>

            {/* Notifications */}
            <Section title="Notifications" subtitle="Choisissez comment être informé">
                <Row label="Notifications email" description="Nouvelles réservations, alertes fidélité">
                    <Toggle checked={notifEmail} onChange={setNotifEmail} />
                </Row>
                <Row label="Notifications push" description="Alertes en temps réel sur l'application">
                    <Toggle checked={notifPush} onChange={setNotifPush} />
                </Row>
                <Row label="SMS" description="Résumé quotidien par SMS (option payante)">
                    <Toggle checked={notifSms} onChange={setNotifSms} />
                </Row>
            </Section>

            {/* Segmentation client (seuils dans segmentRules/{id}, calcul nocturne par Cloud Function) */}
            <SegmentationSection />

            {/* Rapports */}
            <Section title="Rapports & Exports" subtitle="Automatisation des données">
                <Row label="Export mensuel automatique" description="Rapport PDF envoyé le 1er de chaque mois">
                    <Toggle checked={autoExport} onChange={setAutoExport} />
                </Row>
                <Row label="Format des rapports" description="Langue et devise utilisées dans les exports">
                    <select
                        value={language}
                        onChange={e => setLanguage(e.target.value)}
                        className="text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-400 dark:border-gray-700 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                    >
                        <option value="fr">Français — EUR €</option>
                        <option value="en">English — EUR €</option>
                    </select>
                </Row>
            </Section>

            {/* Zone de danger */}
            <Section title="Zone de danger" subtitle="Actions irréversibles">
                <Row label="Supprimer le compte" description="Supprime définitivement votre établissement et toutes les données">
                    <Button variant="danger" size="sm">
                        Supprimer
                    </Button>
                </Row>
            </Section>

            <div className="flex items-center gap-4">
                <Button variant="primary" onClick={handleSave} loading={saving}>
                    {saved ? 'Enregistré' : 'Enregistrer les modifications'}
                </Button>
                {saved && <span className="text-sm text-green-600 font-medium">Paramètres sauvegardés</span>}
            </div>
        </div>
    );
};

export default SettingsPage;
